import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { Observable } from 'rxjs';
import { finalize, timeout } from 'rxjs/operators';

import { LoaderService } from '../loader.service';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  constructor(public loaderService: LoaderService) {}

  // Secondary/background fetches that should never drive the global spinner.
  private readonly silentUrls = [
    '/rest/search/tag-names',
    '/rest/profiles/candidates',
    '/rest/reviews/v2/responses',
    '/rest/reviews/v1/responses',
    '/rest/reviews/V1/myContribution',
    '/rest/profiles/virtualEvent',
    '/rest/profiles/announcements',
  ];

  // Endpoints that may legitimately take a long time — exclude from the safety-net timeout.
  private isLongRunning(url: string): boolean {
    return url.includes('cognitiveservices.azure.com') || url.includes('/upload');
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isExcluded = this.silentUrls.some((u) => req.url.includes(u));
    const suppressed = this.loaderService.disable;

    // Only count requests that actually drive the spinner. Capture the decision
    // per-request so the matching decrement in finalize stays symmetric and the
    // counter (owned by LoaderService) never drifts.
    let counted = false;
    if (!isExcluded && !suppressed) {
      counted = true;
      this.loaderService.requestStarted();
    }

    let stream$ = next.handle(req);
    // File uploads (FormData) can legitimately exceed 60s on slow connections, so
    // exclude them — along with known long-running endpoints — from the timeout.
    const isUpload = req.body instanceof FormData;
    if (!isUpload && !this.isLongRunning(req.url)) {
      // Safety net: a hung request errors out so finalize still runs and the spinner hides.
      stream$ = stream$.pipe(timeout(60000));
    }

    return stream$.pipe(
      finalize(() => {
        if (counted) {
          this.loaderService.requestEnded();
        }
        // Preserve existing one-shot suppression semantics.
        this.loaderService.disable = false;
      })
    );
  }
}
